import { useEffect, useRef } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import {
  CHART_DEFAULT_OPTIONS,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UserUnitSettings,
  UnitConversions,
} from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatNumber, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { DeviceInfo, LINE_COLORS, UnitCategoryId } from '../../common/enums'
import { Viewport } from '../../common/types'
import { getUserUnitSetting } from '../../utils/unitSettings'
import { createAxisLabelFormatter, alertMarkersToStripLines, AlertMarker, getHiddenRangeStripLines } from '../../utils/chartHelpers'

const { CanvasJSChart } = CanvasJSReact

interface Props {
  device: DeviceInfo
  sensorPort: string
  sensorReadings: any
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
  isLoading: boolean
  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
  showHiddenData?: boolean
  alertMarkers?: AlertMarker[]
}

export function VegetronixTHERM200Chart({
  device,
  sensorPort,
  sensorReadings,
  viewport,
  userUnitSettings,
  unitConversions,
  isLoading,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = (timestamp: number) => {},
  showHiddenData = false,
  alertMarkers,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)

  const { precision } = getUserUnitSetting(UnitCategoryId.VolumetricWaterContent, userUnitSettings, unitConversions)


  // Register click listener ONCE when chart mounts (not on every hover)
  // Uses retry mechanism since CanvasJS renders the canvas asynchronously
  // NOTE: CanvasJS creates multiple canvas elements - we need the LAST one (topmost layer)
  useEffect(() => {
    if (!containerRef.current) return

    let canvas: HTMLCanvasElement | null = null
    let retryCount = 0
    const maxRetries = 20
    let timeoutId: ReturnType<typeof setTimeout>

    function onClickHandler() {
      addTimestamp(xRef.current)
    }

    function tryAttachListener() {
      const canvases = containerRef.current?.querySelectorAll('.canvasjs-chart-canvas')
      canvas = canvases && canvases.length > 0 ? (canvases[canvases.length - 1] as HTMLCanvasElement) : null
      if (canvas) {
        canvas.addEventListener('click', onClickHandler)
      } else if (retryCount < maxRetries) {
        retryCount++
        timeoutId = setTimeout(tryAttachListener, 100)
      }
    }

    tryAttachListener()

    return () => {
      clearTimeout(timeoutId)
      if (canvas) {
        canvas.removeEventListener('click', onClickHandler)
      }
    }
  }, [addTimestamp])

  // Get hidden map from sensor data for client-side filtering
  const hiddenMap = sensorReadings?.hidden || {}

  const dataPoints = sensorReadings
    ? Object.keys(sensorReadings.readings)
        .filter(key => !hiddenMap[key])  // Filter out hidden timestamps
        .map((key: string) => {
          const ts = parseInt(key)
          return {
            label: convertTimestamp(ts),
            x: ts * 1000,
            y: roundToDecimal(Number(sensorReadings.readings[ts]), precision),
          }
        })
    : []

  const ghostDataPoints = showHiddenData && sensorReadings?.readings
    ? Object.keys(sensorReadings.readings)
        .map((key: string) => {
          const ts = parseInt(key)
          return {
            label: convertTimestamp(ts),
            x: ts * 1000,
            y: roundToDecimal(Number(sensorReadings.readings[ts]), precision),
          }
        })
    : []

  const hiddenStripLines = showHiddenData ? getHiddenRangeStripLines(hiddenMap) : []

  // const depth = sensorReadings.metadata.sensorConfig.offset

  const line = {
    name: `${sensorPort}`,
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: LINE_COLORS[sensorPort],
    dataPoints,
  }

  // const timestampMin = Math.min(...dataPoints.map((point) => point.x))
  // const timestampMax = Math.max(...dataPoints.map((point) => point.x))

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilTemperature`,
    title: {
      text: 'Soil Temperature',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          if (e.entries[i].dataSeries.name.includes('(hidden)')) continue
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${formatNumber(roundToDecimal(e.entries[i].dataPoint.y, precision))} % </strong><br/>`
        }

        xRef.current = e.entries[0].dataPoint.x

        return content
      },
    },
    axisX: {
      labelFormatter: createAxisLabelFormatter(),
      viewportMinimum: viewport?.minimum || null,
      viewportMaximum: viewport?.maximum || null,
      stripLines: [
        ...selectXRange.map((value) => {
          return {
            value,
            color: 'red',
            labelPlacement: 'outside',
          }
        }),

        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },

        ...hiddenStripLines,
        ...alertMarkersToStripLines(alertMarkers),
      ],
    },
    axisY: {
      includeZero: false,
      maximum: 105,
      minimum: 0,
      title: `Temperature (C)`, // TODO: need to handle unit conversion based on user configuration
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
    },
    // data: [line, fieldCapacityOverlay, permanentWiltingPointOverlay],
    data: [
      ...(showHiddenData && ghostDataPoints.length > 0
        ? [{
            name: `${sensorPort} (hidden)`,
            type: 'line',
            showInLegend: false,
            xValueType: 'dateTime',
            lineThickness: 2,
            color: '#aaaaaa',
            markerSize: 0,
            dataPoints: ghostDataPoints,
            toolTipContent: null,
          }]
        : []),
      line,
    ],
  }

  function getLastReadingTimestamp(): string {
    const timestamps = dataPoints.map((point) => point.x)
    const lastTimestamp = Math.max(...timestamps)
    return new Date(lastTimestamp).toLocaleString()
  }

  const data = [
    [
      `Port ${sensorPort}: ${sensorReadings.metadata.productName}`,
      `${getLastReadingTimestamp()}`,
      `${formatNumber(roundToDecimal(dataPoints[dataPoints.length - 1]?.y, precision) || 0)} %`,
    ],
  ]

  return (
    <CollapsibleSection title="Soil Temperature" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <div ref={containerRef}>
        <CanvasJSChart options={options} />
      </div>
    </CollapsibleSection>
  )
}
